import Page from "../../layout/Page";
import CockpitLayoutComponents from "../../components/CockpitLayoutComponents";
import NotFoundPage from "../NotFoundPage";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { faqBaseUrl } from "../../config/urls";
import Herobox from "../../components/Herobox";
import Breadcrumbs from "../../components/Breadcrumbs";
import FaqPageSection from "../../components/FAQ/FaqPageSection";
import ContentSection from "../../components/ContentSection";
import { getHeroboxBackgroundImagePath } from "../../utils/CmsUtils";
import { usePage } from "../../hooks/CmsHooks";
import FaqItemSearchBox from "../../components/FAQ/FaqItemSearchBox";
import { AnimatePresence, motion } from "motion/react";
import {
  faqUrl,
  getFaqCategoryById,
  getFaqCategoryByLegacyId,
  getFaqCategoryByNameSlug,
  getFaqTagById,
  getFaqTagByLegacyName,
  useFaqData
} from "../../hooks/FaqHooks";
import { useLang } from "../../hooks/LangHooks";
import { FaqCategoryOverview } from "../../components/FAQ/FaqCategoryOverview";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { addNoOverlayState, extractId } from "../../utils/UrlUtils";
import { useNavigationContext } from "../../functional-components/NavigationContext";
import { useEffect } from "react";

const FaqPage = () => {
  const location = useLocation();
  const { registerCurrentPaths, setAvailableOnSites } = useNavigationContext();
  const page = usePage(faqBaseUrl);
  const lang = useLang();
  const faqData = useFaqData(lang);
  const t = useBjelinTranslations();
  const { category: categoryParam, tag: tagParam } = useParams<{
    category: string;
    tag: string;
  }>();

  const curCat =
    getFaqCategoryById(faqData, categoryParam) ||
    getFaqCategoryByNameSlug(faqData, categoryParam) ||
    getFaqCategoryByLegacyId(faqData, categoryParam);
  const curTag =
    getFaqTagById(faqData, extractId(tagParam)) ||
    getFaqTagByLegacyName(faqData, tagParam);

  useEffect(() => {
    if (faqData.articles.length > 0) {
      if (curCat && curTag) {
        setAvailableOnSites(curTag.sites);
        registerCurrentPaths({
          all: faqUrl({ category: curCat, tag: curTag }, true)
        });
      } else if (curCat) {
        setAvailableOnSites(curCat.sites);
        registerCurrentPaths({
          all: faqUrl({ category: curCat }, true)
        });
      } else {
        setAvailableOnSites(faqData.sitesWithFaq);
        registerCurrentPaths({ all: "/faq" });
      }
    }
  }, [
    faqData.articles,
    faqData.sitesWithFaq,
    setAvailableOnSites,
    registerCurrentPaths,
    curCat,
    curTag
  ]);

  if (faqData.articles.length === 0) {
    return <NotFoundPage />;
  }

  const correctUrl = faqUrl({ category: curCat, tag: curTag });
  if (location.pathname !== correctUrl) {
    return <Navigate to={correctUrl} state={addNoOverlayState()} replace />;
  }

  const breadcrumbItems = [{ url: faqBaseUrl, text: t("faq.title") }];

  //The page is expected to have a herobox as the first component. This will not be displayed as page content.
  const heroBkg = getHeroboxBackgroundImagePath(page?.content);

  let title = curCat ? curCat.name : t("faq.title");
  if (curTag) {
    title += `: ${curTag.name}`;
  }
  return (
    <Page name="faq-page" key="faq-page" title={title} scrollToTop="smooth">
      <Herobox
        type={curCat ? "slim" : "fat"}
        textShadow={true}
        className={"text-center"}
        backgroundImage={heroBkg}
      >
        <AnimatePresence mode={"wait"}>
          <motion.div
            key={curCat?.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {curCat ? (
              <h1>{curCat.name}</h1>
            ) : (
              <>
                <h1>{t("faq.title")}</h1>
                <div className="search-area">
                  <FaqItemSearchBox
                    placeholder={t("faq.search.placeholder")}
                    hintText={t("search.hint")}
                    noMatchText={t("faq.search.no-match")}
                  />
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </Herobox>
      {curCat ? (
        <ContentSection
          className="faq-section category-menu-container"
          ariaLabel={curCat?.name}
        >
          <Breadcrumbs items={breadcrumbItems} />
          <FaqPageSection category={curCat} tag={curTag} key={curCat.id} />
        </ContentSection>
      ) : (
        <CockpitLayoutComponents
          components={page.content.slice(heroBkg ? 1 : 0)}
        />
      )}
      <ContentSection
        className="faq-container category-menu-container"
        ariaLabel={t("faq.more-categories")}
      >
        {curCat && <h3>{t("faq.more-categories")}</h3>}
        <div className="category-menu">
          {faqData.categories.map(category => (
            <FaqCategoryOverview category={category} key={category.id} />
          ))}
        </div>
      </ContentSection>
    </Page>
  );
};

export default FaqPage;
